import React, { useState, useEffect, useMemo } from "react";
import { TrendingUp, TrendingDown, ChevronRight, X, Loader2, Package, BarChart3, Calendar, Download, ArrowUpDown } from "lucide-react";
import { api, formatCurrency, formatNumber } from "./api.js";
import useFetch from "./useFetch.js";
import { ApiError, SectionSkeleton, RankBadge, EmptyState, DonutChartWithTooltip, CHART_COLORS, FamilyShareBar, StatusBadge } from '../utils/analyticsHelpers';

/* ============================================================
   Local primitives
   ============================================================ */
function Th({ children, align }) {
  return (
    <th className={`px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ${align === "right" ? "text-right" : "text-left"}`}>
      {children}
    </th>
  );
}

function ShareBar({ pct, color }) {
  return (
    <div className="inline-flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
      </div>
      <span className="w-12 text-right text-xs font-semibold text-slate-700">{Number(pct || 0).toFixed(1)}%</span>
    </div>
  );
}

const TrendPill = ({ value }) => {
  if (value === undefined || value === null || value === 0) return <span className="text-[10px] text-slate-400">—</span>;
  const isUp = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${isUp ? 'text-emerald-600' : 'text-rose-600'}`}>
      {isUp ? <TrendingUp size={10} strokeWidth={3} /> : <TrendingDown size={10} strokeWidth={3} />}
      {Math.abs(value)}%
    </span>
  );
};

const TrendIndicator = ({ value, size = 12 }) => {
  if (value === undefined || value === null) return null;
  const isUp = value >= 0;
  return (
    <div className={`flex items-center gap-1 text-[10px] font-black ${isUp ? 'text-emerald-600' : 'text-rose-600'}`}>
      {isUp ? <TrendingUp size={size} strokeWidth={3} /> : <TrendingDown size={size} strokeWidth={3} />}
      <span>{isUp ? '+' : ''}{value}%</span>
    </div>
  );
};

/* ============================================================
   MAIN COMPONENT — Table view like SalesByRegion
   ============================================================ */
export function CustomerRanking({ query, groupBy, onFamilyClick, currentParams }) {
  const [selectedCustomer, setSelectedCustomer] = useState(null); // Now stores the full row object
  const [viewMode, setViewMode] = useState("summary"); 

  if (query.loading) return <SectionSkeleton withPie />;
  if (query.error) return <ApiError error={query.error} label="Customer Ranking" />;

  const data = query.data || {};
  console.log('data: ',data);
  const rows = data.rows || [];
  const totals = data.totals || {};

  // Timezone-agnostic exact year extraction from the 'from' parameter
  const fromDateStr = currentParams?.from || "";
  let contextYear = new Date().getFullYear();
  if (data.meta?.actualYear) {
    contextYear = data.meta.actualYear;
  } else if (fromDateStr) {
    const parsedYear = parseInt(fromDateStr.substring(0, 4), 10);
    if (!isNaN(parsedYear)) contextYear = parsedYear;
  }

  const meta = {
    ...(data.meta || {}),
    ActualYear: contextYear,
    lyYearLabel: contextYear - 1,
    yblYearLabel: contextYear - 2,
  };

  if (!rows.length) return <EmptyState label="No data." />;

  const isFamilyView = groupBy === "family";

  return (
    <>
      <div className="p-6 space-y-4">
        {/* Section-level toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <BarChart3 size={14} className="text-indigo-500" />
            {isFamilyView ? "Customer Families" : "Customers"} — Revenue Ranking
          </div>

          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
            <button
              onClick={() => setViewMode("summary")}
              className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition ${
                viewMode === "summary" ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              Summary
            </button>
            <button
              onClick={() => setViewMode("monthly")}
              className={`px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition inline-flex items-center gap-1 ${
                viewMode === "monthly" ? "bg-indigo-600 text-white shadow" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Calendar size={11} />
              Monthly
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
          {/* ---- Donut (STICKY) ---- */}
          <div className="lg:sticky lg:top-6 lg:self-start flex flex-col items-center justify-center rounded-xl bg-slate-50 p-4">
            <DonutChartWithTooltip
              data={rows.map(c => ({
                label: (isFamilyView ? (c.family || c.customer || "") : (c.customer || "")).toUpperCase(),
                value: c.revenue,
                color: c.color,
                revenue: c.revenue,
                unitsSold: c.unitsSold || 0,
              }))}
              centerLabel={String(meta.ActualYear)}
              centerValue="SHARES"
            />
          </div>

          {/* ---- Table ---- */}
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            {viewMode === "summary" ? (
              <SummaryTable
                rows={rows}
                meta={meta}
                totals={totals}
                isFamilyView={isFamilyView}
                onRowClick={(c) => {
                  const name = isFamilyView ? (c.family || c.customer) : c.customer;
                  if (isFamilyView) onFamilyClick(name, currentParams);
                  else setSelectedCustomer(c);
                }}
              />
            ) : (
              <MonthlyTable
                rows={rows}
                meta={meta}
                isFamilyView={isFamilyView}
                onRowClick={(c) => {
                  const name = isFamilyView ? (c.family || c.customer) : c.customer;
                  if (isFamilyView) onFamilyClick(name, currentParams);
                  else setSelectedCustomer(c);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Single Customer Drilldown Modal */}
      {selectedCustomer && (
        <CustomerDrilldownModal
          customerData={selectedCustomer}
          isFamily={false}
          currentParams={currentParams}
          meta={meta}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </>
  );
}

/* ============================================================
   SUMMARY TABLE — Group/Main Dashboard Level
   Now includes Units VS LY and Units VS YBL
   ============================================================ */
function SummaryTable({ rows, meta, totals, isFamilyView, onRowClick }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50 sticky top-0 z-10">
        <tr>
          <Th>#</Th>
          <Th>{isFamilyView ? "Customer Family" : "Customer"}</Th>
          <Th align="right">Revenue ({meta.ActualYear})</Th>
          <Th align="right">VS {meta.lyYearLabel}</Th>
          <Th align="right">VS {meta.yblYearLabel}</Th>
          <Th align="right">Units ({meta.ActualYear})</Th>
          <Th align="right">Units VS {meta.lyYearLabel}</Th>
          <Th align="right">Units VS {meta.yblYearLabel}</Th>
          <Th align="right">% Share</Th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((c) => {
          const displayName = isFamilyView ? (c.family || c.customer || "") : (c.customer || "");
          return (
            <tr
              key={c.rank}
              onClick={() => onRowClick(c)}
              className="cursor-pointer transition hover:bg-slate-50/60"
            >
              <td className="px-4 py-3"><RankBadge n={c.rank} /></td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: c.color }} />
                  <span className="font-semibold text-slate-900 uppercase text-xs">{displayName}</span>
                </div>
              </td>
              {/* Revenue Block */}
              <td className="px-4 py-3 text-right font-bold text-slate-900">{formatCurrency(c.revenue)}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-slate-500">{formatCurrency(c.lyRevenue || 0)}</span>
                  <TrendPill value={c.trend ?? c.revTrendLY} />
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-slate-500">{formatCurrency(c.yblRevenue || 0)}</span>
                  <TrendPill value={c.trendYBL ?? c.revTrendYBL} />
                </div>
              </td>
              
              {/* Units Block */}
              <td className="px-4 py-3 text-right font-bold text-slate-800">{formatNumber(c.unitsSold)}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-slate-500">{formatNumber(c.lyUnits ?? c.unitsSoldLY ?? 0)}</span>
                  <TrendPill value={c.unitsTrendLY ?? c.unitsVsLY} />
                </div>
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-slate-500">{formatNumber(c.yblUnits ?? c.unitsSoldYBL ?? 0)}</span>
                  <TrendPill value={c.unitsTrendYBL ?? c.unitsVsYBL} />
                </div>
              </td>

              <td className="px-4 py-3 text-right">
                <ShareBar pct={c.sharecy ?? c.share ?? 0} color={c.color} />
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot className="border-t-2 border-slate-200 bg-slate-50">
        <tr>
          <td />
          <td className="px-4 py-2.5 font-bold text-slate-800">Total</td>
          <td className="px-4 py-2.5 text-right font-bold">{formatCurrency(totals.revenue || 0)}</td>
          <td className="px-4 py-2.5 text-right">
            <div className="flex flex-col items-end">
              <span className="text-[11px] text-slate-500">{formatCurrency(totals.history?.[0]?.revenue || totals.lyRevenue || 0)}</span>
              <TrendPill value={totals.history?.[0]?.trend ?? totals.revTrendLY} />
            </div>
          </td>
          <td className="px-4 py-2.5 text-right">
            <div className="flex flex-col items-end">
              <span className="text-[11px] text-slate-500">{formatCurrency(totals.history?.[1]?.revenue || totals.yblRevenue || 0)}</span>
              <TrendPill value={totals.history?.[1]?.trend ?? totals.revTrendYBL} />
            </div>
          </td>
          <td className="px-4 py-2.5 text-right font-bold">{formatNumber(totals.unitsSold || 0)}</td>
          <td className="px-4 py-2.5 text-right">
            <div className="flex flex-col items-end">
              <span className="text-[11px] text-slate-500">{formatNumber(totals.history?.[0]?.unitsSold ?? totals.lyUnits ?? 0)}</span>
              <TrendPill value={totals.unitsTrendLY} />
            </div>
          </td>
          <td className="px-4 py-2.5 text-right">
            <div className="flex flex-col items-end">
              <span className="text-[11px] text-slate-500">{formatNumber(totals.history?.[1]?.unitsSold ?? totals.yblUnits ?? 0)}</span>
              <TrendPill value={totals.unitsTrendYBL} />
            </div>
          </td>
          <td className="px-4 py-2.5 text-right font-bold">100%</td>
        </tr>
      </tfoot>
    </table>
  );
}

/* ============================================================
   MONTHLY TABLE — section-level view
   ============================================================ */
function MonthlyTable({ rows, meta, isFamilyView, onRowClick }) {
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const { lyYearLabel, yblYearLabel } = meta || {};

  const columnTotals = useMemo(() => {
    const init = { cyRev: 0, lyRev: 0, yblRev: 0, cyUnits: 0, lyUnits: 0, yblUnits: 0 };
    const totals = Array(12).fill(null).map(() => ({ ...init }));
    rows.forEach(r => {
      (r.monthlyBreakdown || []).forEach((m, i) => {
        totals[i].cyRev += Number(m?.revenue) || 0;
        totals[i].lyRev += Number(m?.lyRevenue) || 0;
        totals[i].yblRev += Number(m?.yblRevenue) || 0;
        totals[i].cyUnits += Number(m?.unitsSold) || 0;
        totals[i].lyUnits += Number(m?.lyUnits) || 0;
        totals[i].yblUnits += Number(m?.yblUnits) || 0;
      });
    });
    return totals;
  }, [rows]);

  const grandTotalRev = columnTotals.reduce((a, b) => a + b.cyRev, 0);
  const grandTotalUnits = columnTotals.reduce((a, b) => a + b.cyUnits, 0);

  return (
    <div className="group/container relative bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
      <div className="h-1.5 w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
      <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300">
        <table className="w-full text-sm border-collapse min-w-[1600px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] w-16">Rank</th>
              <th className="px-6 py-5 text-left text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Growth Entity</th>
              {MONTHS.map(m => (
                <th key={m} className="px-4 py-5 text-center">
                  <span className="bg-slate-900 text-white px-3 py-1 rounded-full text-[10px] font-black tracking-tighter uppercase">{m}</span>
                </th>
              ))}
              <th className="px-8 py-5 text-right text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] bg-indigo-50/50">Annual Aggregation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((c) => {
              const displayName = c.customer || "Unknown";
              const monthly = c.monthlyBreakdown || [];
              const rowTotalRev = monthly.reduce((s, m) => s + (Number(m?.revenue) || 0), 0);
              const rowTotalUnits = monthly.reduce((s, m) => s + (Number(m?.unitsSold) || 0), 0);

              return (
                <tr key={c.rank} onClick={() => onRowClick(c)} className="group cursor-pointer transition-all duration-200 hover:bg-indigo-50/30">
                  <td className="px-6 py-6 text-center">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-indigo-600 group-hover:text-white transition-colors duration-300 font-black text-xs text-slate-500">
                      {c.rank}
                    </div>
                  </td>
                  <td className="px-6 py-6 border-l-4" style={{ borderLeftColor: c.color }}>
                    <div className="flex flex-col">
                      <span className="font-black text-slate-900 uppercase text-[11px] leading-none mb-1 tracking-tight">{displayName}</span>
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{isFamilyView ? 'Family Account' : 'Direct Channel'}</span>
                    </div>
                  </td>
                  {MONTHS.map((_, i) => {
                    const m = monthly[i] || {};
                    const cyRev = Number(m.revenue || 0);
                    const lyRev = Number(m.lyRevenue || 0);
                    const yblRev = Number(m.yblRevenue || 0);
                    const cyU = Number(m.unitsSold || 0);
                    return (
                      <td key={i} className="px-4 py-6 text-center tabular-nums">
                        <div className="mb-3">
                          <div className="text-[13px] font-black text-slate-900 leading-none">{cyRev ? formatCurrency(cyRev) : "—"}</div>
                          <div className="inline-block mt-1 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[9px] font-black uppercase">{cyU ? `${formatNumber(cyU)} Units` : "No Vol"}</div>
                        </div>
                        <div className="pt-2 border-t border-slate-100 space-y-1">
                          <div className="flex justify-between items-center gap-2">
                             <span className="text-[8px] font-bold text-slate-400">{lyYearLabel}</span>
                             <span className="text-[9px] font-bold text-slate-600">{lyRev ? formatCurrency(lyRev) : "—"}</span>
                          </div>
                          <div className="flex justify-between items-center gap-2">
                             <span className="text-[8px] font-bold text-slate-400">{yblYearLabel}</span>
                             <span className="text-[9px] font-bold text-slate-400">{yblRev ? formatCurrency(yblRev) : "—"}</span>
                          </div>
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-8 py-6 text-right bg-indigo-50/20 border-l border-indigo-100">
                    <div className="text-[14px] font-black text-indigo-900 leading-none mb-1">{formatCurrency(rowTotalRev)}</div>
                    <div className="text-[10px] text-indigo-500 font-black uppercase tracking-tighter mb-2">{formatNumber(rowTotalUnits)} Total Units</div>
                    <div className="flex justify-end items-center gap-2 pt-2 border-t border-indigo-100/50">
                       <div className="flex items-center gap-1"><span className="text-[8px] font-black text-slate-400">YOY</span><TrendIndicator value={c.revTrendLY} size={10} /></div>
                       <div className="flex items-center gap-1"><span className="text-[8px] font-black text-slate-400">2YR</span><TrendIndicator value={c.revTrendYBL} size={10} /></div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-900">
            <tr>
              <td colSpan={2} className="px-6 py-8">
                <div className="flex flex-col">
                  <span className="text-[11px] font-black text-white uppercase tracking-[0.3em]">Matrix Totals</span>
                  <span className="text-[9px] font-bold text-slate-500 uppercase italic">Aggregated Performance Index</span>
                </div>
              </td>
              {columnTotals.map((v, i) => (
                <td key={i} className="px-4 py-8 text-center tabular-nums border-l border-slate-800">
                  <div className="text-[12px] font-black text-white mb-1">{formatCurrency(v.cyRev)}</div>
                  <div className="text-[9px] font-black text-indigo-400 uppercase mb-3">{formatNumber(v.cyUnits)} Units</div>
                  <div className="space-y-1 opacity-50">
                    <div className="text-[8px] font-bold text-slate-400 flex justify-between px-2"><span>LY</span><span>{formatCurrency(v.lyRev)}</span></div>
                    <div className="text-[8px] font-bold text-slate-500 flex justify-between px-2"><span>2Y</span><span>{formatCurrency(v.yblRev)}</span></div>
                  </div>
                </td>
              ))}
              <td className="px-8 py-8 text-right bg-indigo-600">
                <div className="text-[16px] font-black text-white leading-none mb-1">{formatCurrency(grandTotalRev)}</div>
                <div className="text-[11px] text-indigo-200 font-black uppercase">{formatNumber(grandTotalUnits)} Units</div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ============================================================
   CUSTOMER DRILLDOWN MODAL — Single Customer Context
   ============================================================ */
function CustomerDrilldownModal({ customerData, isFamily, currentParams, meta, onClose, user }) {
  const customerName = customerData?.customer || customerData?.family || "Unknown Customer";
  
  const [tab, setTab] = useState("orders");
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [isSorting, setIsSorting] = useState(false);

  useEffect(() => {
    setSortConfig({ key: 'revenue', direction: 'desc' });
  }, [tab]);

  const ordersQuery = useFetch(() => api.customerOrders(customerName, currentParams), [customerName, currentParams.from, tab === 'orders']);
  const productsQuery = useFetch(() => api.customerProducts(customerName, currentParams), [customerName, currentParams.from, tab === 'products']);

  const canExport = user?.role === 'admin' || user?.permissions?.canExport;
  const EMPTY_ARRAY = [];
  
  const rawOrders = useMemo(() => ordersQuery.data?.orders ?? EMPTY_ARRAY, [ordersQuery.data?.orders]);  
  const ordersSummary = ordersQuery.data?.summary || { totalRevenue: 0, totalUnitsSold: 0 };
  const rawProducts = useMemo(() => productsQuery.data?.products ?? EMPTY_ARRAY, [productsQuery.data?.products]);
  const productTotals = productsQuery.data?.totals || { revenue: 0, unitsSold: 0 };

  const toggleSort = (type) => {
    setIsSorting(true);
    let key = '';
    if (type === 'alpha') key = tab === 'orders' ? 'salesOrderNumber' : 'name';
    else if (type === 'revenue') key = tab === 'orders' ? 'amount' : 'revenue';
    else if (type === 'units') key = tab === 'orders' ? 'totalQuantity' : 'qty';

    const direction = sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc';
    
    setTimeout(() => {
      setSortConfig({ key, direction });
      setIsSorting(false);
    }, 400);
  };

  const sortedData = useMemo(() => {
    const dataPool = tab === 'orders' ? [...rawOrders] : [...rawProducts];
    if (!dataPool.length) return [];
    return dataPool.sort((a, b) => {
      let aVal = a[sortConfig.key] ?? 0;
      let bVal = b[sortConfig.key] ?? 0;
      if (typeof aVal === 'string') return sortConfig.direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [rawOrders, rawProducts, tab, sortConfig]);

  const isActive = (type) => {
    if (type === 'alpha') return sortConfig.key === (tab === 'orders' ? 'salesOrderNumber' : 'product');
    if (type === 'revenue') return sortConfig.key === 'revenue';
    if (type === 'units') return sortConfig.key === (tab === 'orders' ? 'units' : 'unitsSold');
    return false;
  };

  const rev = customerData?.revenue || 0;
  const revLY = customerData?.lyRevenue ?? customerData?.revenueLY ?? 0;
  const revYBL = customerData?.yblRevenue ?? customerData?.revenueYBL ?? 0;
  const revTrendLY = customerData?.trend ?? customerData?.revTrendLY ?? customerData?.revenueVsLY ?? 0;
  const revTrendYBL = customerData?.trendYBL ?? customerData?.revTrendYBL ?? customerData?.revenueVsYBL ?? 0;

  const units = customerData?.unitsSold ?? customerData?.qty ?? 0;
  const unitsLY = customerData?.lyUnits ?? customerData?.unitsSoldLY ?? 0;
  const unitsYBL = customerData?.yblUnits ?? customerData?.unitsSoldYBL ?? 0;
  const unitsTrendLY = customerData?.unitsTrendLY ?? customerData?.unitsVsLY ?? 0;
  const unitsTrendYBL = customerData?.unitsTrendYBL ?? customerData?.unitsVsYBL ?? 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      {/* ADDED: h-[95vh] for mobile so it doesn't overflow screen */}
      <div className="flex h-[95vh] md:h-full md:max-h-[900px] w-full max-w-[1240px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl animate-in zoom-in-95">
        
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-6 py-4 shrink-0">
          <div className="flex items-center gap-4">
            <div>
              <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">{customerName}</h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5 tracking-widest">
                {isFamily ? "Family Context" : "Customer Context"} · {currentParams.from} → {currentParams.to}
              </p>
            </div>
            {isSorting && (
              <div className="flex items-center gap-2 px-3 py-1 bg-indigo-600 rounded-full animate-pulse hidden md:flex">
                <div className="w-2 h-2 bg-white rounded-full animate-bounce" />
                <span className="text-[9px] font-black text-white italic uppercase">Processing Sort...</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-200 text-slate-500 transition-colors"><X size={20} /></button>
        </div>

        {/* TOP LEVEL KPIs - ADDED horizontal scroll for mobile to save vertical space */}
        <div className="flex overflow-x-auto snap-x md:grid md:grid-cols-3 gap-4 px-6 py-4 bg-slate-50/50 border-b border-slate-100 shrink-0 scrollbar-hide">
          <div className="min-w-[85vw] sm:min-w-[280px] md:min-w-0 shrink-0 snap-center">
            <SummaryTile
              label={`Revenue (${meta.ActualYear})`}
              value={formatCurrency(rev)}
              sub={`vs ${meta.lyYearLabel}: ${formatCurrency(revLY)}`}
              trend={revTrendLY}
              color="indigo"
            />
          </div>
          <div className="min-w-[85vw] sm:min-w-[280px] md:min-w-0 shrink-0 snap-center">
            <SummaryTile
              label={`Units (${meta.ActualYear})`}
              value={formatNumber(units)}
              sub={`vs ${meta.lyYearLabel}: ${formatNumber(unitsLY)}`}
              trend={unitsTrendLY}
              color="emerald"
            />
          </div>
          <div className="min-w-[85vw] sm:min-w-[280px] md:min-w-0 shrink-0 snap-center">
            <SummaryTile
              label={`VS ${meta.yblYearLabel}`}
              value={formatCurrency(revYBL)}
              sub={`${formatNumber(unitsYBL)} units`}
              trend={revTrendYBL}
              color="sky"
            />
          </div>
        </div>

        {/* Reactive Toolbar */}
        <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-100 px-5 py-3 bg-white gap-4 shrink-0">
          <div className="inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5 whitespace-nowrap">
            <button onClick={() => setTab("orders")} className={`px-4 py-1.5 rounded-md text-[11px] font-black uppercase tracking-wider transition inline-flex items-center gap-2 ${tab === "orders" ? "bg-white text-indigo-600 shadow" : "text-slate-500 hover:text-slate-700"}`}><BarChart3 size={13} /> Sales Orders</button>
            <button onClick={() => setTab("products")} className={`px-4 py-1.5 rounded-md text-[11px] font-black uppercase tracking-wider transition inline-flex items-center gap-2 ${tab === "products" ? "bg-white text-indigo-600 shadow" : "text-slate-500 hover:text-slate-700"}`}><Package size={13} /> Product Performance</button>
          </div>
          {/* ADDED horizontal scrolling for mobile toolbar buttons */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 scrollbar-hide w-full md:w-auto">
            <button onClick={() => toggleSort('alpha')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-black border transition-all uppercase whitespace-nowrap ${isActive('alpha') ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'}`}><ArrowUpDown size={12}/> {tab === 'orders' ? 'Order Number' : 'Product Name (A-Z)'}</button>
            <button onClick={() => toggleSort('revenue')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-black border transition-all uppercase whitespace-nowrap ${isActive('revenue') ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'}`}><ArrowUpDown size={12}/> Revenue $</button>
            <button onClick={() => toggleSort('units')} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-black border transition-all uppercase whitespace-nowrap ${isActive('units') ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-400 hover:border-slate-300'}`}><ArrowUpDown size={12}/> Units Sold</button>
            <div className="hidden md:block w-px h-4 bg-slate-200 mx-1" />
            {canExport && <button className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-md text-[10px] font-black hover:bg-slate-800 uppercase transition-colors whitespace-nowrap"><Download size={12} /> Export CSV</button>}
          </div>
        </div>

        {/* Scrolling Viewport */}
        <div className="flex-1 overflow-auto bg-slate-50 relative scrollbar-hide">
          {tab === "orders" ? (
            <OrdersTab revenue ={formatCurrency(rev)} loading={ordersQuery.loading} error={ordersQuery.error} orders={sortedData} summary={ordersSummary} />
          ) : (
            <ProductsTab revenue ={formatCurrency(rev)} loading={productsQuery.loading} error={productsQuery.error} products={sortedData} totals={productTotals} meta={meta} />
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-100 p-4 bg-slate-50 text-right shrink-0">
          <button onClick={onClose} className="px-8 py-2.5 bg-slate-900 text-white rounded-lg text-xs font-black uppercase hover:bg-slate-800 shadow-lg tracking-wider transition-all active:scale-95">Close Analysis</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Orders Tab (SO View) ---- */
function OrdersTab({ revenue, loading, error, orders, summary }) {
  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center space-y-4">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Gathering Data...</span>
      </div>
    );
  }
  if (error) return <ApiError error={error} />;
  if (!orders.length) return <EmptyState label="No orders in this period." />;

  const { totalRevenue = 0, totalUnits = 0, totalOrders = 0 } = summary;

  return (
    <div className="space-y-6 p-6">
      <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase border-b border-slate-200">
            <tr>
              <Th>DATE</Th>
              <Th>#SO</Th>
              <Th>PO (TAG)</Th>
              <Th align="right">Status</Th>
              <Th align="right">Quantity</Th>
              <Th align="right">Amount</Th>                            
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {orders.map((c, i) => (
              <tr key={i} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-bold text-slate-800 uppercase text-[11px]">
                  {c.orderDateTime ? new Date(c.orderDateTime).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "-"}
                </td>                 
                <td className="px-4 py-3 font-bold text-slate-800 uppercase text-[11px]">#{c.salesOrderNumber}</td>
                <td className="px-4 py-3 font-bold text-slate-800 uppercase text-[11px]">{c.tag || 'N/A'}</td>
                <td className="px-4 py-3 text-right"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-3 text-right font-bold text-slate-800 text-[11px]">{formatNumber(c.totalQuantity)}</td>
                <td className="px-4 py-3 text-right font-black text-slate-900">{formatCurrency(c.amount)}</td>               
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50">
            <tr>
              <td className="px-4 py-2.5 font-bold text-slate-800">Total ({orders.length} orders)</td>
              <td></td>
              <td></td>
              <td></td>
              <td className="px-4 py-2.5 text-right font-bold text-slate-800">{formatNumber(totalUnits)}</td>
              <td className="px-4 py-2.5 text-right font-bold text-slate-900">{revenue}</td>
              <td />
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ---- Products Tab (with year-over-year comparison) ---- */
function ProductsTab({ revenue, loading, error, products, totals, meta }) {
  if (loading) {
    return (
      <div className="py-20 flex flex-col items-center justify-center space-y-4">
        <Loader2 className="animate-spin text-indigo-600" size={32} />
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Loading Products...</span>
      </div>
    );
  }
  if (error) return <ApiError error={error} />;
  if (!products.length) return <EmptyState label="No products sold in this period." />;

  return (
    <div className="space-y-6 p-6">
      <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] font-bold text-slate-500 uppercase border-b border-slate-200">
            <tr>
              <Th>SKU / Item</Th>
              <Th align="right">Revenue ({meta.ActualYear})</Th>
              <Th align="right">vs {meta.lyYearLabel}</Th>
              <Th align="right">vs {meta.yblYearLabel}</Th>
              <Th align="right">Units</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {products.map((p, i) => (
              <tr key={i} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="text-[11px] font-black text-slate-800 uppercase leading-tight">{p.name || p.description}</span>
                    <code className="text-[9px] text-indigo-500 font-bold mt-0.5">{p.sku}</code>
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-black text-slate-900">{formatCurrency(p.revenue)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] text-slate-500">{formatCurrency(p.lyRevenue || 0)}</span>
                    <TrendPill value={p.revTrendLY} />
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex flex-col items-end">
                    <span className="text-[11px] text-slate-500">{formatCurrency(p.yblRevenue || 0)}</span>
                    <TrendPill value={p.revTrendYBL} />
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-bold text-slate-600">{formatNumber(p.qty || p.unitsSold || 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50">
            <tr>
              <td className="px-4 py-2.5 font-bold text-slate-800">Total ({products.length} products)</td>
              <td className="px-4 py-2.5 text-right font-bold text-slate-900">{revenue}</td>
              <td className="px-4 py-2.5 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-slate-500">{formatCurrency(totals.lyRevenue || 0)}</span>
                  <TrendPill value={totals.revTrendLY} />
                </div>
              </td>
              <td className="px-4 py-2.5 text-right">
                <div className="flex flex-col items-end">
                  <span className="text-[11px] text-slate-500">{formatCurrency(totals.yblRevenue || 0)}</span>
                  <TrendPill value={totals.revTrendYBL} />
                </div>
              </td>
              <td className="px-4 py-2.5 text-right font-bold">{formatNumber(totals.unitsSold || 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ============================================================
   Extended Summary Tile
   ============================================================ */
function SummaryTile({ label, value, sub, trend, color }) {
  const colors = {
    indigo: "text-indigo-600 bg-indigo-50/50 border-indigo-100",
    emerald: "text-emerald-600 bg-emerald-50/50 border-emerald-100",
    sky: "text-sky-600 bg-sky-50/50 border-sky-100"
  };
  return (
    <div className={`rounded-xl p-4 border shadow-sm ${colors[color]}`}>
      <p className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-1">{label}</p>
      <p className="text-xl font-black">{value}</p>
      {sub && (
        <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-current/10">
          <span className="text-[10px] font-bold opacity-60">{sub}</span>
          {trend !== undefined && <TrendPill value={trend} />}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Customer Family Drilldown Modal
   ============================================================ */
export function CustomerFamilyDrilldownModal({ familyName, params, onClose }) {
  const [selectedCustomer, setSelectedCustomer] = useState(null); 
  const [activeTab, setActiveTab] = useState("customers"); 
  const [tabLoading, setTabLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const drillParams = useMemo(() => {
    const p = { ...params };
    delete p.family;
    delete p.customer;
    return p;
  }, [params]);

  const depKey = `drilldown|${familyName}|${drillParams.from}|${drillParams.to}|${drillParams.country}|${drillParams.region}|${drillParams.channel}`;

  const drillQ = useFetch(
    () => api.customerFamilyDrilldown(familyName, drillParams),
    [depKey]
  );

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    setCustomerSearch("");
    setOrderSearch("");
    setProductSearch("");
  }, [familyName, activeTab]);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setTabLoading(true);
    setActiveTab(tab);
    const t = setTimeout(() => setTabLoading(false), 280);
    return () => clearTimeout(t);
  };

  const data = drillQ.data;
  const customers = data?.customers || [];
  const totalRevenue = data?.totalRevenue || 0;
  const familyProductRanking = data?.familyProductRanking || [];
  const familyOrders = data?.familyOrders || [];

  const fromDateStr = drillParams.from || "";
  let actualYear = data?.meta?.actualYear || new Date().getFullYear();
  if (data?.meta?.actualYear) {
    actualYear = data.meta.actualYear;
  } else if (fromDateStr) {
    const parsedYear = parseInt(fromDateStr.substring(0, 4), 10);
    if (!isNaN(parsedYear)) actualYear = parsedYear;
  }
  const lyYear = data?.meta?.lyYear || actualYear - 1;
  const yblYear = data?.meta?.yblYear || actualYear - 2;

  const inferredMeta = {
    ActualYear: actualYear,
    lyYearLabel: lyYear,
    yblYearLabel: yblYear,
  };

  const filteredCustomers = useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c) => String(c.customer || "").toLowerCase().includes(q));
  }, [customers, customerSearch]);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return familyOrders;
    return familyOrders.filter(
      (o) => String(o.salesOrderNumber || "").toLowerCase().includes(q) || String(o.poNumber || "").toLowerCase().includes(q) || String(o.customer || "").toLowerCase().includes(q)
    );
  }, [familyOrders, orderSearch]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return familyProductRanking;
    return familyProductRanking.filter(
      (p) => String(p.sku || "").toLowerCase().includes(q) || String(p.name || "").toLowerCase().includes(q)
    );
  }, [familyProductRanking, productSearch]);

  const renderPct = (value) => {
    const n = Number(value);
    if (n === 0 || Number.isNaN(n)) return <span className="text-[10px] text-slate-400">0%</span>;
    const positive = n > 0;
    return (
      <span className={`inline-flex items-center gap-0.5 font-bold ${positive ? "text-emerald-600" : "text-rose-500"}`}>
        {positive ? <TrendingUp size={10} strokeWidth={3} /> : <TrendingDown size={10} strokeWidth={3} />}
        {Math.abs(n).toFixed(2)}%
      </span>
    );
  };

  const TabSkeleton = () => (
    <div className="p-6 space-y-3 animate-pulse">
      <div className="h-9 w-64 rounded-lg bg-slate-100" />
      <div className="h-10 w-full rounded-xl bg-slate-100" />
      {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-11 w-full rounded-lg bg-slate-50" />)}
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* ADDED: h-[95vh] constrained height so flex-1 works correctly */}
        <div className="relative w-full max-w-[1400px] h-[95vh] md:h-auto md:max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col">
          
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm">
                <button onClick={onClose} className="text-slate-400 hover:text-indigo-600 font-medium transition hidden md:block">Customer Families</button>
                <ChevronRight size={14} className="text-slate-300 hidden md:block" />
                <span className="font-bold text-slate-900">{familyName}</span>
              </div>
              {(drillQ.loading || tabLoading) && <span className="text-[10px] font-bold text-amber-500 animate-pulse uppercase tracking-wider">• Loading...</span>}
            </div>
            <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition"><X size={18} /></button>
          </div>

          {/* Group Level KPIs - ADDED Mobile Horizontal Scroll */}
          {!drillQ.loading && data && (
            <div className="flex overflow-x-auto snap-x md:grid md:grid-cols-3 gap-4 px-6 py-4 bg-slate-50/50 border-b border-slate-100 shrink-0 scrollbar-hide">
              <div className="min-w-[85vw] sm:min-w-[280px] md:min-w-0 shrink-0 snap-center">
                <SummaryTile
                  label={`Revenue (${inferredMeta.ActualYear})`}
                  value={formatCurrency(data.totalRevenue || 0)}
                  sub={`vs ${inferredMeta.lyYearLabel}: ${formatCurrency(data.totalRevenueLY || 0)}`}
                  trend={data.revenueVsLY}
                  color="indigo"
                />
              </div>
              <div className="min-w-[85vw] sm:min-w-[280px] md:min-w-0 shrink-0 snap-center">
                <SummaryTile
                  label={`Units (${inferredMeta.ActualYear})`}
                  value={formatNumber(data.totalUnitsSold || 0)}
                  sub={`vs ${inferredMeta.lyYearLabel}: ${formatNumber(data.totalUnitsSoldLY || 0)}`}
                  trend={data.unitsVsLY}
                  color="emerald"
                />
              </div>
              <div className="min-w-[85vw] sm:min-w-[280px] md:min-w-0 shrink-0 snap-center">
                <SummaryTile
                  label={`VS ${inferredMeta.yblYearLabel}`}
                  value={formatCurrency(data.totalRevenueYBL || 0)}
                  sub={`${formatNumber(data.totalUnitsSoldYBL || 0)} units`}
                  trend={data.revenueVsYBL}
                  color="sky"
                />
              </div>
            </div>
          )}

          {/* Tab Toggle - ADDED Mobile Horizontal Scroll */}
          {!drillQ.loading && data && customers.length > 0 && (
            <div className="flex items-center gap-2 px-6 pt-4 shrink-0 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => handleTabChange("customers")} disabled={tabLoading}
                className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition whitespace-nowrap ${activeTab === "customers" ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"} ${tabLoading ? "opacity-70 cursor-wait" : ""}`}
              >Customers</button>
              <button
                onClick={() => handleTabChange("orders")} disabled={tabLoading}
                className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition whitespace-nowrap ${activeTab === "orders" ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"} ${tabLoading ? "opacity-70 cursor-wait" : ""}`}
              >Sales Orders</button>
              <button
                onClick={() => handleTabChange("products")} disabled={tabLoading}
                className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition whitespace-nowrap ${activeTab === "products" ? "bg-indigo-600 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"} ${tabLoading ? "opacity-70 cursor-wait" : ""}`}
              >Product Ranking</button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto bg-slate-50">
            {drillQ.loading ? (
              <div className="p-6 space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-12 w-full animate-pulse rounded-xl bg-slate-100" />)}</div>
            ) : drillQ.error ? (
              <div className="p-6"><ApiError error={drillQ.error} label={`${familyName} drill-down`} /></div>
            ) : customers.length === 0 ? (
              <div className="p-6"><EmptyState label={`No sales data found for ${familyName} in the selected period.`} /></div>
            ) : tabLoading ? (
              <TabSkeleton />
            ) : activeTab === "customers" ? (
              /* ── TAB 1: Customer Family Data ── */
              <div className="p-6 space-y-4">
                <div className="relative max-w-sm">
                  <input
                    type="text" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} placeholder="Search customer..."
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-9 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition"
                  />
                  <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
                  {customerSearch && <button onClick={() => setCustomerSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
                  <div className="lg:sticky flex flex-col items-center justify-center rounded-xl bg-white border border-slate-200 p-4 self-start shadow-sm w-full">
                    <DonutChartWithTooltip
                      data={customers.slice(0, 10).map((c, i) => ({ label: c.customer, value: c.revenue, color: CHART_COLORS[i % CHART_COLORS.length], revenue: c.revenue, unitsSold: c.unitsSold }))}
                      centerLabel={familyName.toUpperCase()}
                      centerValue={formatCurrency(totalRevenue)}
                    />
                    <div className="mt-3 w-full space-y-1">
                      {customers.slice(0, 10).map((c, i) => (
                        <div key={c.customer} className="flex items-center gap-2 text-xs text-slate-600">
                          <span className="inline-block h-2 w-2 flex-shrink-0 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="truncate font-medium">{c.customer}</span>
                          <span className="ml-auto font-black text-slate-800">{c.percentOfFamily}%</span>
                        </div>
                      ))}
                      {customers.length > 6 && <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest text-center pt-2">+{customers.length - 6} more customers</p>}
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm w-full">
                    <table className="min-w-full w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-500 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left">#</th>
                          <th className="px-4 py-3 text-left">Customer</th>
                          <th className="px-4 py-3 text-right">Revenue ({actualYear})</th>
                          <th className="px-4 py-3 text-right whitespace-nowrap">VS {lyYear}</th>
                          <th className="px-4 py-3 text-right whitespace-nowrap">VS {yblYear}</th>
                          <th className="px-4 py-3 text-right">Units ({actualYear})</th>
                          <th className="px-4 py-3 text-right whitespace-nowrap">Units VS {lyYear}</th>
                          <th className="px-4 py-3 text-right whitespace-nowrap">Units VS {yblYear}</th>
                          <th className="px-4 py-3 text-right">% of Family</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredCustomers.length === 0 ? (
                          <tr><td colSpan={9} className="px-4 py-8 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">No customers match “{customerSearch}”</td></tr>
                        ) : (
                          filteredCustomers.map((c) => (
                            <tr key={c.customer} className="hover:bg-slate-50/60 cursor-pointer transition" onClick={() => setSelectedCustomer(c)} title={`Click to view orders/products for ${c.customer}`}>
                              <td className="px-4 py-3"><RankBadge n={c.rank} /></td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="inline-block h-2 w-2 flex-shrink-0 rounded-full" style={{ background: CHART_COLORS[(c.rank - 1) % CHART_COLORS.length] }} />
                                  <span className="font-bold text-slate-800 uppercase text-xs whitespace-nowrap">{c.customer}</span>
                                </div>
                              </td>
                              
                              <td className="px-4 py-3 text-right font-black text-slate-900">{formatCurrency(c.revenue)}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5">
                                  <span className="text-[11px] font-medium text-slate-500">{formatCurrency(c.revenueLY || 0)}</span>
                                  {renderPct(c.revenueVsLY)}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5">
                                  <span className="text-[11px] font-medium text-slate-500">{formatCurrency(c.revenueYBL || 0)}</span>
                                  {renderPct(c.revenueVsYBL)}
                                </div>
                              </td>

                              <td className="px-4 py-3 text-right font-black text-slate-700">{formatNumber(c.unitsSold)}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5">
                                  <span className="text-[11px] font-medium text-slate-500">{formatNumber(c.unitsSoldLY || 0)}</span>
                                  {renderPct(c.unitsVsLY)}
                                </div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5">
                                  <span className="text-[11px] font-medium text-slate-500">{formatNumber(c.unitsSoldYBL || 0)}</span>
                                  {renderPct(c.unitsVsYBL)}
                                </div>
                              </td>

                              <td className="px-4 py-3 text-right">
                                <FamilyShareBar pct={c.percentOfFamily} color={CHART_COLORS[(c.rank - 1) % CHART_COLORS.length]} />
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                      <tfoot className="bg-indigo-50/60 border-t-2 border-indigo-100">
                        <tr>
                          <td className="px-4 py-3" />
                          <td className="px-4 py-3 text-xs font-bold uppercase text-indigo-700 whitespace-nowrap">{familyName} Total</td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-indigo-700">{formatCurrency(data?.totalRevenue || 0)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-[11px] font-medium text-slate-500">{formatCurrency(data?.totalRevenueLY || 0)}</span>
                              {renderPct(data?.revenueVsLY)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-[11px] font-medium text-slate-500">{formatCurrency(data?.totalRevenueYBL || 0)}</span>
                              {renderPct(data?.revenueVsYBL)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-indigo-700">{formatNumber(data?.totalUnitsSold || 0)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-[11px] font-medium text-slate-500">{formatNumber(data?.totalUnitsSoldLY || 0)}</span>
                              {renderPct(data?.unitsVsLY)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-[11px] font-medium text-slate-500">{formatNumber(data?.totalUnitsSoldYBL || 0)}</span>
                              {renderPct(data?.unitsVsYBL)}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-bold text-indigo-700">{data?.percentage}%</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            ) : activeTab === "orders" ? (
              /* ── TAB 2: Sales Orders List ── */
              <div className="p-6 space-y-4">
                <div className="relative max-w-sm">
                  <input
                    type="text" value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} placeholder="Search SO, PO #, or Customer..."
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-9 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition"
                  />
                  <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
                  {orderSearch && <button onClick={() => setOrderSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-indigo-50/60 border-b border-indigo-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wide text-indigo-700">Sales Orders</h4>
                        <p className="text-[10px] text-slate-500 mt-0.5">All orders across {familyName}</p>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{filteredOrders.length}{orderSearch ? ` / ${familyOrders.length}` : ""} Orders</span>
                    </div>
                  </div>

                  {filteredOrders.length > 0 ? (
                    <div className="overflow-x-auto w-full">
                      <table className="min-w-full w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-500">
                          <tr>
                            <th className="px-4 py-3 text-left">Date</th>
                            <th className="px-4 py-3 text-left">SO #</th>
                            <th className="px-4 py-3 text-left">PO # (Tag)</th>
                            <th className="px-4 py-3 text-left">Customer</th>
                            <th className="px-4 py-3 text-center">Status</th>
                            <th className="px-4 py-3 text-right">Units</th>
                            <th className="px-4 py-3 text-right">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredOrders.map((order) => (
                            <tr key={order.salesOrderNumber} className="hover:bg-slate-50 transition">
                              <td className="px-4 py-3 text-slate-600 font-bold text-[11px] uppercase whitespace-nowrap">
                                {order.orderDateTime ? new Date(order.orderDateTime).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "-"}
                              </td>
                              <td className="px-4 py-3 font-black text-indigo-600 whitespace-nowrap">{order.salesOrderNumber}</td>
                              <td className="px-4 py-3 text-slate-700 font-bold text-[11px] uppercase whitespace-nowrap">{order.poNumber || "-"}</td>
                              <td className="px-4 py-3 text-slate-800 font-bold text-[11px] uppercase whitespace-nowrap">{order.customer}</td>
                              <td className="px-4 py-3 text-center"><span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wide bg-slate-100 text-slate-600 uppercase">{order.status || "-"}</span></td>
                              <td className="px-4 py-3 text-right font-semibold text-slate-700">{formatNumber(order.unitsSold)}</td>
                              <td className="px-4 py-3 text-right font-black text-slate-900">{formatCurrency(order.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      {orderSearch ? `No orders match “${orderSearch}”` : "No order data available"}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              /* ── TAB 3: Family Product Ranking ── */
              <div className="p-6 space-y-4">
                <div className="relative max-w-sm">
                  <input
                    type="text" value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search name or SKU..."
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 pl-9 text-sm text-slate-800 placeholder:text-slate-400 outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition"
                  />
                  <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" /></svg>
                  {productSearch && <button onClick={() => setProductSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600"><X size={14} /></button>}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                  <div className="px-4 py-3 bg-indigo-50/60 border-b border-indigo-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-xs font-bold uppercase tracking-wide text-indigo-700">Product Ranking</h4>
                        <p className="text-[10px] text-slate-500 mt-0.5">
                          All products purchased across {familyName} {" · "} <span className="font-semibold text-slate-600">{actualYear}</span> {" vs "} <span className="font-semibold text-slate-600">{lyYear}</span> {" / "} <span className="font-semibold text-slate-600">{yblYear}</span>
                        </p>
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 uppercase">{filteredProducts.length}{productSearch ? ` / ${familyProductRanking.length}` : ""} Products</span>
                    </div>
                  </div>

                  {filteredProducts.length > 0 ? (
                    <div className="overflow-x-auto w-full">
                      <table className="min-w-full w-full text-sm">
                        <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-500 border-b border-slate-200">
                          <tr>
                            <th className="px-4 py-3 text-left">#</th>
                            <th className="px-4 py-3 text-left">Name / SKU</th>
                            <th className="px-4 py-3 text-right">Revenue ({actualYear})</th>
                            <th className="px-4 py-3 text-right whitespace-nowrap">VS {lyYear}</th>
                            <th className="px-4 py-3 text-right whitespace-nowrap">VS {yblYear}</th>
                            <th className="px-4 py-3 text-right">Units ({actualYear})</th>
                            <th className="px-4 py-3 text-right whitespace-nowrap">Units VS {lyYear}</th>
                            <th className="px-4 py-3 text-right whitespace-nowrap">Units VS {yblYear}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredProducts.map((product) => (
                            <tr key={product.sku} className="hover:bg-slate-50 transition">
                              <td className="px-4 py-3"><RankBadge n={product.rank} /></td>
                              <td className="px-4 py-3">
                                <span className="font-bold text-slate-800 uppercase text-xs whitespace-nowrap">{product.name || "N/A"}</span>
                                <span className="block text-[10px] text-indigo-500 font-bold mt-0.5">{product.sku}</span>
                              </td>
                              <td className="px-4 py-3 text-right font-black text-slate-900">{formatCurrency(product.revenue)}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5"><span className="text-[11px] font-medium text-slate-500">{formatCurrency(product.revenueLY || 0)}</span>{renderPct(product.revenueVsLY)}</div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5"><span className="text-[11px] font-medium text-slate-500">{formatCurrency(product.revenueYBL || 0)}</span>{renderPct(product.revenueVsYBL)}</div>
                              </td>
                              <td className="px-4 py-3 text-right font-bold text-slate-800">{formatNumber(product.unitsSold)}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5"><span className="text-[11px] font-medium text-slate-500">{formatNumber(product.unitsSoldLY || 0)}</span>{renderPct(product.unitsVsLY)}</div>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex flex-col items-end gap-0.5"><span className="text-[11px] font-medium text-slate-500">{formatNumber(product.unitsSoldYBL || 0)}</span>{renderPct(product.unitsVsYBL)}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                      {productSearch ? `No products match “${productSearch}”` : "No product data available"}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedCustomer && (
        <CustomerDrilldownModal
          customerData={selectedCustomer}
          isFamily={false}
          currentParams={drillParams}
          meta={inferredMeta}
          onClose={() => setSelectedCustomer(null)}
        />
      )}
    </>
  );
}